import { ChangeDetectionStrategy, Component, computed, effect, inject, Injector, input, output, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { SEARCH_PARSER } from '../../app/shared/services/search-parser.token';
import { SearchFilterSpec } from '../../app/shared/models/search-filter-spec.model';
import { detectUnsupportedQuestion, UnsupportedQuestionPattern } from '../../app/shared/services/unsupported-question-detector';
import { SearchSuggestionsStore } from '../../app/shared/state/search-suggestions.store';
import { SearchHistoryStore } from '../../app/shared/state/search-history.store';
import { SearchUnmatchedQueryLogStore } from '../../app/shared/state/search-unmatched-query-log.store';
import { SEARCH_EXAMPLES, Suggestion, SuggestionGroupKey } from '../../app/shared/models/search-suggestions.model';

/** Maps a suggestion group's key to its heading's i18n dictionary key — kept as one small
    lookup here rather than a `@switch` scattered through the template. */
const GROUP_HEADING_KEYS: Record<SuggestionGroupKey, string> = {
  status: 'header.intentSearchGroupStatus',
  location: 'header.intentSearchGroupLocation',
  licenseType: 'header.intentSearchGroupLicenseType',
  department: 'header.intentSearchGroupDepartment',
  people: 'header.intentSearchGroupPeople',
  goTo: 'header.intentSearchGroupGoTo',
};

/**
 * Results dropdown for the header's intent search. Purely additive to
 * TopBarComponentComponent: takes the live query text as an input, emits the user's chosen
 * interpretation, and exposes `selectHighlighted()` so the host's existing Enter-key handler
 * can try this first before falling back to the pre-existing plain-text search.
 */
@Component({
  selector: 'app-intent-search',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './app-intent-search.component.html',
  styleUrl: './app-intent-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntentSearchComponent {
  private readonly parser = inject(SEARCH_PARSER);
  private readonly injector = inject(Injector);
  private readonly translate = inject(TranslateService);
  private readonly unmatchedLog = inject(SearchUnmatchedQueryLogStore);
  readonly history = inject(SearchHistoryStore);
  readonly suggestions = inject(SearchSuggestionsStore);

  readonly query = input<string>('');
  readonly resultSelect = output<SearchFilterSpec>();
  /** Fires when the user picks a suggestion or a recent-search entry (Task 7) — the host
      cannot write back into its own `query` input, so this is how the picked text flows back
      down: the host mirrors it into `queryControl`, which flows back into `[query]`. */
  readonly querySelect = output<string>();

  readonly interpretations = signal<SearchFilterSpec[]>([]);
  readonly highlightedIndex = signal(0);
  readonly highlightedSpec = computed<SearchFilterSpec | null>(
    () => this.interpretations()[this.highlightedIndex()] ?? null,
  );
  readonly hasQuery = computed(() => this.query().trim().length > 0);
  readonly showNoMatch = computed(() => this.hasQuery() && this.interpretations().length === 0);

  /** Whether the search box currently has keyboard focus — driven by the host's `onFocus()`/
      `onBlur()` forwarding (see TopBarComponentComponent), not a local DOM listener, since this
      component owns no `<input>` of its own. */
  readonly focused = signal(false);
  /** Suggestions/recent-searches panel is shown only while focused with nothing typed yet —
      the moment a character is typed (or focus leaves), it yields to the no-match/results/
      unsupported branches below it in the template's `@if`/`@else if` chain. */
  readonly showSuggestions = computed(() => this.focused() && !this.hasQuery());
  /** Set by the query-driven effect below when the CURRENT query matches one of the curated
      "the deterministic parser fundamentally can't answer this" shapes — `null` otherwise. */
  readonly unsupportedMatch = signal<UnsupportedQuestionPattern | null>(null);
  readonly searchExamples = SEARCH_EXAMPLES;

  constructor() {
    // The vocabulary this dropdown searches against is primed by AppComponent
    // (see AppComponent.refreshSearchVocabulary()), not here — this component's header
    // instance is constructed once and never re-mounts across navigations, so priming from
    // its own constructor could never pick up a post-login identity change.
    effect(() => {
      const q = this.query();
      if (!q.trim()) {
        this.interpretations.set([]);
        this.highlightedIndex.set(0);
        this.unsupportedMatch.set(null);
        return;
      }
      // Checked FIRST, before the parser ever runs: a query matching one of these curated
      // shapes ("which department has the worst backlog", "why is X low", …) can never resolve
      // to a real interpretation no matter what the vocabulary contains, so there is no point
      // spending a parse pass on it.
      const pattern = detectUnsupportedQuestion(q);
      if (pattern) {
        this.unsupportedMatch.set(pattern);
        this.interpretations.set([]);
        this.highlightedIndex.set(0);
        return;
      }
      this.unsupportedMatch.set(null);
      this.parser.parse(q).subscribe((specs) => {
        this.interpretations.set(specs);
        this.highlightedIndex.set(0);
      });
    }, { injector: this.injector });
  }

  onArrowDown(): void {
    const count = this.interpretations().length;
    if (count === 0) return;
    this.highlightedIndex.update((i) => (i + 1) % count);
  }

  onArrowUp(): void {
    const count = this.interpretations().length;
    if (count === 0) return;
    this.highlightedIndex.update((i) => (i - 1 + count) % count);
  }

  /** Focusing the (empty) box calls the suggestions store unconditionally on every focus — the
      store itself dedupes per identity (`SearchSuggestionsStore.ensureLoaded`'s own fetch-once
      latch), so this component does not need (and must not duplicate) its own "have I already
      asked" tracking. */
  onFocus(): void {
    this.focused.set(true);
    this.suggestions.ensureLoaded();
  }

  /** Blurring away from a currently-unmatched or currently-unsupported query is one of the two
      points (alongside Enter, in {@link selectHighlighted}) this component logs the query text
      via {@link maybeLogUnmatched} — never from the query-driven effect itself, so typing
      through several intermediate no-match states on the way to a match never logs anything. */
  onBlur(): void {
    this.maybeLogUnmatched();
    this.focused.set(false);
  }

  onRowClick(spec: SearchFilterSpec): void {
    this.resultSelect.emit(spec);
    this.history.record(this.query());
  }

  /** Called by the host on Enter. Returns true (and emits) when a row is highlighted, false
      when the dropdown is empty — the host falls back to plain-text search on false. */
  selectHighlighted(): boolean {
    // Runs BEFORE the "nothing highlighted -> host falls back to plain-text search" branch
    // below, so it observes the exact same no-match/unsupported state that branch is about to
    // act on.
    this.maybeLogUnmatched();
    const spec = this.highlightedSpec();
    if (!spec) return false;
    this.resultSelect.emit(spec);
    this.history.record(this.query());
    return true;
  }

  /** A suggestion or recent-search click behaves like clicking an already-interpreted dropdown
      row: immediate navigation on one click, not "populate the box and make the user act
      again". `querySelect` lets the host mirror the picked text into the visible box; the top
      spec the real parser resolves it to is emitted via the existing `resultSelect`. */
  onSelectQuery(query: string): void {
    this.querySelect.emit(query);
    this.parser.parse(query).subscribe((specs) => {
      if (specs.length > 0) {
        this.resultSelect.emit(specs[0]);
        // A suggestion/recent-search pick is a resolved, user-picked interpretation exactly
        // like a results-dropdown row — record() fires from every path that funnels through
        // resultSelect (onRowClick, selectHighlighted, and this one), mirroring the pattern
        // those two already use.
        this.history.record(query);
      }
    });
  }

  rowLabel(spec: SearchFilterSpec): string {
    const fields = spec.matchedTerms.map((t) => t.label).join(' · ');
    const destination = this.translate.instant(
      spec.route === 'reports' ? 'header.intentSearchDestinationReports' : 'header.intentSearchDestinationWorkspace',
    );
    return `${fields} → ${destination}`;
  }

  groupHeadingKey(key: SuggestionGroupKey): string {
    return GROUP_HEADING_KEYS[key];
  }

  /** The "closest supported search" fallback shown under the unsupported-question message: the
      pattern's own related group when it names one, else the generic examples line — wrapped as
      `Suggestion`-shaped items so the template's `(click)="onSelectQuery(item.query)"` works
      identically in both branches. */
  closestGroupItems(pattern: UnsupportedQuestionPattern): Suggestion[] {
    return pattern.relatedGroup
      ? (this.suggestions.groups().find((g) => g.key === pattern.relatedGroup)?.items ?? [])
      : this.searchExamples.map((q) => ({ query: q, label: q }));
  }

  /** The `SuggestionGroupKey` {@link closestGroupItems} sourced its items from — `null` for the
      generic-examples fallback (those items have no real group). Kept for the unsupported-
      question branch's template call site (`suggestionLabel(closestGroupKey(...), item)`),
      though `suggestionLabel` itself now branches on `item.labelKey` rather than this group
      key — see that method's own doc (review fix round 1, Task 16). */
  closestGroupKey(pattern: UnsupportedQuestionPattern): SuggestionGroupKey | null {
    return pattern.relatedGroup ?? null;
  }

  /** `GO_TO_SUGGESTIONS`' two items carry `labelKey` (never `label`) — see
      `Suggestion`/`GO_TO_SUGGESTIONS`'s own docs (`search-suggestions.model.ts`). Every other
      group's `label` already comes from `SearchVocabularyStore`'s live data and is rendered
      as-is. Branching on `item.labelKey` (not a `groupKey === 'goTo'` special case) means a
      FUTURE static, translated group needs no change here — it just sets `labelKey` on its own
      items and this method resolves it the same way. */
  suggestionLabel(groupKey: SuggestionGroupKey | null, item: Suggestion): string {
    if (item.labelKey) return this.translate.instant(item.labelKey);
    return item.label ?? '';
  }

  private maybeLogUnmatched(): void {
    const q = this.query().trim();
    if (!q) return;
    if (this.unsupportedMatch() || (this.hasQuery() && this.interpretations().length === 0)) {
      this.unmatchedLog.record(this.query());
    }
  }
}
